import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
  'data-id'?: string;
}
export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'max-w-lg',
  'data-id': dataId
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Handle focus trap and return focus
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      firstElement?.focus();
      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };
      document.addEventListener('keydown', handleTab);
      return () => {
        document.removeEventListener('keydown', handleTab);
        previousFocusRef.current?.focus();
      };
    }
  }, [isOpen]);
  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-584" data-mp="3f6nmil9i5nuy">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0F172A]/50 backdrop-blur-sm transition-opacity duration-200" onClick={onClose} aria-hidden="true" data-id="element-585" data-mp="3k46yfeiq9ojq" />

      {/* Modal Content */}
      <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="modal-title" className={`relative w-full ${maxWidth} bg-white rounded-xl shadow-xl transform transition-all duration-200 flex flex-col max-h-[90vh]`} data-id="element-586" data-mp="2rhckz1kbvpfr">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]" data-id="element-587" data-mp="31c4ik5dwucaj">
          <h3 id="modal-title" className="text-[22px] font-semibold text-[#0F172A] leading-tight" data-id="element-588" data-mp="brj68cp7baxc">
            {title}
          </h3>
          <button onClick={onClose} className="p-2 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors duration-200" aria-label="Close modal" data-id="element-589" data-mp="216k5gsmi3wkg">
            <X size={20} data-id="element-590" data-mp="1rghcgsfdqim4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto text-[#475569] text-[16px] leading-relaxed" data-id="element-591" data-mp="1re4o9xj13c0m">
          {children}
        </div>

        {/* Footer */}
        {footer && <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] rounded-b-xl flex justify-end gap-3" data-id="element-592" data-mp="1igklgk5ip7nw">
            {footer}
          </div>}
      </div>
    </div>;
};